import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getAnamnesisApiErrorMessage } from '@/api/patient-anamnesis';
import { patientsApi } from '@/api/patients';
import { type SaveStatus } from '@/components/system/SaveIndicator';
import Loading from '@/components/ui/Loading';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { usePatientAnamnesis, useSaveAnamnesis } from '@/hooks/use-patient-anamnesis';
import WrapperLayout from '@/pages/components/WrapperLayout';
import { PatientDetailPage } from '@/pages/constants';
import AnamnesisHeader from './AnamnesisHeader';
import { buildTemplateHtml } from './template-html';
import { getTemplateById, type AnamnesisTemplateId } from './templates';
import { useAnamnesisActions } from './use-anamnesis-actions';

const AUTOSAVE_DEBOUNCE_MS = 1500;

// TipTap normalizes an empty document to '<p></p>'; treat both as "nothing written"
// so merely opening a blank editor doesn't autosave an empty draft.
const isBlankDoc = (html: string) => html === '' || html === '<p></p>';
const isSameContent = (a: string, b: string) => a === b || (isBlankDoc(a) && isBlankDoc(b));

export default function AnamnesisEditorPage() {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationState = location.state as {
        templateId?: AnamnesisTemplateId;
        replaceExisting?: boolean;
    } | null;
    const templateId = navigationState?.templateId;
    // Set when the user chose "Recomeçar do zero": the template replaces whatever
    // was written before instead of yielding to it.
    const replaceExisting = Boolean(navigationState?.replaceExisting && templateId);

    const { data: anamnesis, isLoading } = usePatientAnamnesis(patientId);
    const saveMutation = useSaveAnamnesis(patientId);
    const { remove } = useAnamnesisActions(patientId);

    // Reuse the cached patient overview (shared queryKey with the detail page) for
    // the header name and the read-only patient card in the rail.
    const { data: overview } = useQuery({
        queryKey: ['patients', patientId, 'overview'],
        queryFn: () => patientsApi.getOverview(patientId || ''),
        enabled: Boolean(patientId),
    });
    const patient = overview?.patient;
    const patientName = patient?.full_name;

    const [content, setContent] = useState<string | null>(null);
    const [status, setStatus] = useState<SaveStatus>('saved');

    const initializedRef = useRef(false);
    const lastSavedRef = useRef('');
    const contentRef = useRef('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const deletedRef = useRef(false);

    const persist = useCallback(
        async (html: string) => {
            setStatus('saving');
            try {
                await saveMutation.mutateAsync({ content_html: html });
                lastSavedRef.current = html;
                // Only flip to "saved" if nothing newer was typed meanwhile.
                setStatus(contentRef.current === html ? 'saved' : 'unsaved');
            } catch (error) {
                setStatus('error');
                // Actionable rejections (content too long, plan limit) deserve the
                // server's own message — a silent "error" badge won't explain them.
                const message = getAnamnesisApiErrorMessage(error);
                if (message) toast.error(message, { id: 'anamnesis-save-error' });
            }
        },
        [saveMutation],
    );

    // Initialize the editor once the anamnesis query has settled. An uploaded
    // anamnesis counts too: its extracted text prefills the editor, and the
    // first edit converts it to a written (inline) one via the regular PUT.
    useEffect(() => {
        if (initializedRef.current || isLoading) return;

        const hasExisting = Boolean(anamnesis?.content_html);
        const templateHtml = templateId ? buildTemplateHtml(getTemplateById(templateId)) : '';

        const initial =
            hasExisting && !replaceExisting ? (anamnesis?.content_html ?? '') : templateHtml;

        setContent(initial);
        contentRef.current = initial;
        lastSavedRef.current = initial;
        initializedRef.current = true;

        // Restarting discards saved content, so write the fresh template out now
        // rather than leaving the old anamnesis in the database until the first
        // keystroke (or forever, if the user just closes the tab).
        if (hasExisting && replaceExisting) {
            void persist(initial);
        }
    }, [isLoading, anamnesis, templateId, replaceExisting, persist]);

    const handleChange = useCallback(
        (html: string) => {
            setContent(html);
            contentRef.current = html;

            if (debounceRef.current) clearTimeout(debounceRef.current);

            if (isSameContent(html, lastSavedRef.current)) {
                setStatus('saved');
                return;
            }

            setStatus('unsaved');
            debounceRef.current = setTimeout(() => {
                void persist(html);
            }, AUTOSAVE_DEBOUNCE_MS);
        },
        [persist],
    );

    // Warn before leaving with unsaved changes.
    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            if (!deletedRef.current && !isSameContent(contentRef.current, lastSavedRef.current)) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // Flush pending changes on unmount (best-effort) — unless the anamnesis was
    // just deleted, in which case re-saving would resurrect it.
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (
                !deletedRef.current &&
                initializedRef.current &&
                !isSameContent(contentRef.current, lastSavedRef.current)
            ) {
                saveMutation.mutate({ content_html: contentRef.current });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goBack = () => {
        navigate(`/${generatePath(PatientDetailPage, { patientId: patientId ?? '' })}`);
    };

    const handleDelete = () => {
        remove(() => {
            deletedRef.current = true;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            goBack();
        });
    };

    return (
        <WrapperLayout title="Anamnese" subtitle="" showHeader={false} width="full">
            <div className="w-full">
                <AnamnesisHeader
                    patient={patient}
                    patientName={patientName}
                    status={status}
                    canDelete={Boolean(anamnesis)}
                    onRetry={() => void persist(contentRef.current)}
                    onBack={goBack}
                    onDelete={handleDelete}
                />

                {isLoading || content === null ? (
                    <Loading />
                ) : (
                    <div className="mx-auto w-[80%] max-w-[90rem]">
                        <div className="flex min-h-[calc(100svh-9rem)] flex-col overflow-hidden rounded-xl border border-border/70 bg-card shadow-elevation-1">
                            <RichTextEditor
                                value={content}
                                onChange={handleChange}
                                placeholder="Comece a escrever a anamnese deste paciente…"
                                showWordCount
                                enableBubbleMenu
                                autoFocus
                                fillHeight
                                editorClassName="dark:prose-invert px-6 py-5 sm:px-8 sm:py-6"
                                className="flex-1 border-0 bg-transparent shadow-none"
                                viewportClassName="flex-1"
                            />
                        </div>
                    </div>
                )}
            </div>
        </WrapperLayout>
    );
}
